import { useEffect, useRef } from 'react';
import Markdown from 'react-markdown';
import { useChatStore } from '../../store/useChatStore';
import { ActionCard } from './ActionCard';
import { ConfirmationCard } from './ConfirmationCard';
import { TypingIndicator } from './TypingIndicator';
import { SuggestionChips } from './SuggestionChips';

interface ChatMessageListProps {
  onSuggestionSelect: (text: string) => void;
  focusChatInput?: () => void;
}

export function ChatMessageList({ onSuggestionSelect, focusChatInput }: ChatMessageListProps) {
  const messages = useChatStore((s) => s.messages);
  const isLoading = useChatStore((s) => s.isLoading);
  const error = useChatStore((s) => s.error);
  const confirmActions = useChatStore((s) => s.confirmActions);
  const rejectActions = useChatStore((s) => s.rejectActions);
  const rejectActionsWithContext = useChatStore((s) => s.rejectActionsWithContext);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  if (messages.length === 0 && !isLoading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <p className="text-lg font-semibold text-foreground mb-1">Hi! I'm Tidy</p>
        <p className="text-sm text-muted-foreground mb-4">
          I can help set up your cleaning tasks
        </p>
        <SuggestionChips onSelect={onSuggestionSelect} />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
      {messages.map((msg) => (
        <div key={msg.id}>
          {/* Message bubble */}
          <div
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              data-role={msg.role}
              className={`max-w-[85%] rounded-3xl px-4 py-2.5 text-[15px] leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-primary text-primary-foreground rounded-br-md whitespace-pre-wrap'
                  : 'bg-surface text-foreground rounded-bl-md chat-markdown'
              }`}
            >
              {msg.role === 'assistant' ? (
                <Markdown>{msg.content}</Markdown>
              ) : (
                msg.content
              )}
            </div>
          </div>

          {/* Executed action cards */}
          {msg.executedActions && msg.executedActions.length > 0 && (
            <div className="mt-2 space-y-1.5 ml-0 mr-8">
              {msg.executedActions.map((action, i) => (
                <ActionCard key={i} action={action} />
              ))}
            </div>
          )}

          {/* Pending confirmation card */}
          {msg.pendingActions && msg.pendingActions.length > 0 && (
            <div className="mt-2 ml-0 mr-8">
              <ConfirmationCard
                actions={msg.pendingActions}
                onConfirm={() => confirmActions(msg.id)}
                onReject={() => rejectActions(msg.id)}
                onSuggestChanges={focusChatInput ? () => {
                  rejectActionsWithContext(msg.id);
                  focusChatInput();
                } : undefined}
                disabled={isLoading}
              />
            </div>
          )}
        </div>
      ))}

      {isLoading && <TypingIndicator />}

      {error && (
        <div className="px-3 py-2 rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
